import { Injectable } from '@angular/core';
import { MenuItemModel } from '@shared/models/menu-item.model';
import { BehaviorSubject } from 'rxjs';

interface CartItem {
  id: string;
  price: number;
  notes?: string; // Optional notes field
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private cartProductsSubject = new BehaviorSubject<CartItem[]>([]);
  cartProducts$ = this.cartProductsSubject.asObservable();
  totalPrice: number = 0;
  cartQuantity: number = 0;
  private cartExpiryTime = 10 * 60 * 1000; // 10 minutes in milliseconds

  constructor() {
    this.checkCartExpiry();
    this.getCartProduct();
  }

  getCartProduct() {
    this.fetchCartData();
    this.updateCartQuantity();
    this.updateTotalPrice();
  }

  private fetchCartData() {
    const cartData = localStorage.getItem('cart');
    const cartTimestamp = localStorage.getItem('cartTimestamp');
    const now = new Date().getTime();

    if (cartData && cartTimestamp) {
      const timestamp = parseInt(cartTimestamp, 10);
      if (now - timestamp < this.cartExpiryTime) {
        this.cartProductsSubject.next(this.parseCartData(cartData));
      } else {
        this.clearCart();
      }
    } else {
      this.cartProductsSubject.next([]);
    }
  }

  updateTotalPrice() {
    const currentCart = this.cartProductsSubject.getValue();
    this.totalPrice = currentCart.reduce((acc, item) => acc + item.price, 0);
  }

  updateCartQuantity() {
    const currentCart = this.cartProductsSubject.getValue();
    this.cartQuantity = currentCart.length;
  }

  addToCart(event: MenuItemModel, notes: string | null) {
    const currentCart = [...this.cartProductsSubject.getValue()];
    const item: CartItem = {
      id: event.id,
      price: event.price,
    };
    if (notes != null) {
      item['notes'] = notes;
    }
    currentCart.push(item);
    this.cartProductsSubject.next(currentCart);
    this.saveCartToLocalStorage(currentCart);
    this.updateCartQuantity();
    this.updateTotalPrice();
  }

  removeFromCart(id: string) {
    const currentCart = [...this.cartProductsSubject.getValue()];
    const index = currentCart.map(item => item.id).lastIndexOf(id);
    if (index > -1) {
      currentCart.splice(index, 1); // remove the last occurrence of the item with the specified id
      this.cartProductsSubject.next(currentCart);
      this.saveCartToLocalStorage(currentCart);
      this.updateCartQuantity();
      this.updateTotalPrice();
    }
  }

  removeGroupFromCart(id: string, notes?: string) {
    const currentCart = this.cartProductsSubject.getValue();
    const normalizedNotes = notes || '';
    const nextCart = currentCart.filter(item => item.id !== id || (item.notes || '') !== normalizedNotes);

    this.cartProductsSubject.next(nextCart);
    this.saveCartToLocalStorage(nextCart);
    this.updateCartQuantity();
    this.updateTotalPrice();
  }

  removeSingleFromGroup(id: string, notes?: string) {
    const currentCart = [...this.cartProductsSubject.getValue()];
    const normalizedNotes = notes || '';
    const index = currentCart.map(item => `${item.id}-${item.notes || ''}`).lastIndexOf(`${id}-${normalizedNotes}`);

    if (index > -1) {
      currentCart.splice(index, 1);
      this.cartProductsSubject.next(currentCart);
      this.saveCartToLocalStorage(currentCart);
      this.updateCartQuantity();
      this.updateTotalPrice();
    }
  }

  countItem(id: string) {
    const currentCart = this.cartProductsSubject.getValue();
    if (currentCart.length < 1) {
      return 0;
    } else {
      return currentCart.reduce((count, item) => (item.id === id ? count + 1 : count), 0);
    }
  }

  private saveCartToLocalStorage(cart: CartItem[]) {
    localStorage.setItem('cart', JSON.stringify(cart));
    localStorage.setItem('cartTimestamp', new Date().getTime().toString());
  }

  private parseCartData(cartData: string): CartItem[] {
    try {
      const parsed = JSON.parse(cartData);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      console.warn('Invalid cart data found in localStorage', error);
    }

    this.clearCart();
    return [];
  }

  private checkCartExpiry() {
    const now = new Date().getTime();
    const cartTimestamp = localStorage.getItem('cartTimestamp');

    if (cartTimestamp) {
      const timestamp = parseInt(cartTimestamp, 10);
      if (now - timestamp >= this.cartExpiryTime) {
        this.clearCart();
      }
    }
  }

  private clearCart() {
    localStorage.removeItem('cart');
    localStorage.removeItem('cartTimestamp');
    this.cartProductsSubject.next([]);
    this.updateCartQuantity();
    this.updateTotalPrice();
  }
}
